import { useEffect, useMemo } from 'react';

import {
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { VENUES_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';

import { GetServerSideProps } from 'next';
import queryString from 'query-string';
import { Provider } from 'react-redux';

import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import { UserContextProvider } from '~/contexts/UserContext';
import { findSlugsInPath } from '~/hooks/usePathSlugs';
import { redirect } from '~/libs/client/util/nextUtils';
import { getNavHtml } from '~/libs/server/api/nav';
import { fetchStorefrontSearchResults } from '~/libs/server/api/storefronts';
import { getSearchableFacets } from '~/libs/server/api/taxonomy';
import { getUserContext } from '~/libs/server/api/user';
import ApiService from '~/libs/server/util/apiService';
import { getSearchLocationForSlug } from '~/libs/universal/vendorMarket';
import { fetchPreAuthSearchResultsPageData } from '~/libs/universal/vendorSearch';
import { getSearchFilters } from '~/pages/couples/explore/components/SearchResults/components/SearchControls/searchFilters';
import {
  PRE_AUTH_PAGE_SIZE,
  PreAuthSearchResultsPageProvider,
} from '~/pages/couples/explore/components/SearchResults/contexts/PreAuthSearchResultsPageProvider';
import { PreAuthSearchResultsSeoProvider } from '~/pages/couples/explore/components/SearchResults/contexts/PreAuthSearchResultsSeoProvider';
import {
  PreAuthSearchResultsPageProviderProps,
  SearchResultPageSearchResult,
} from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import PreAuthSearchResultsPage from '~/pages/couples/explore/components/SearchResults/PreAuthSearchResultsPage';
import { shouldRedirect } from '~/pages/couples/explore/components/SearchResults/utils/pageProviderHelpers';
import { buildBackendVendorSearchRequestFromSearchProperties } from '~/pages/couples/explore/util/buildSearchRequest';
import {
  locationAndCategorySlugInPath,
  parseVendorSearchUrl,
} from '~/pages/couples/explore/util/parseVendorSearchUrl';
import { getStore } from '~/store';
import { IUserContext } from '~/types/userContext';
import EXPERIMENT_FLAGS, { EXPERIMENT_FLAGS_SSR } from '~/util/experimentFlags';
import { getFacetsByVendorType } from '~/util/facetUtils';
import { ALL_MARKETS } from '~/util/searchUtils';
import { isVendorIdentified } from '~/util/vendorIdentification';

import styles from './slug.module.less';

export type PreAuthSearchResultsPageProps = PreAuthSearchResultsPageProviderProps & {
  navHtml: string | null;
  userAgent: string | undefined;
  userContext: IUserContext | null;
};

const Page = (props: PreAuthSearchResultsPageProps) => {
  const { userAgent, navHtml, parsedUrl, userContext, identifiedAsVendor } = props;
  const store = useMemo(() => getStore(), []);

  const { activeSecondaryLink, activeTertiaryLink } = useMemo(() => {
    const { vendorSearch } = parsedUrl;
    const { selectedTaxonomy } = vendorSearch;
    return {
      activeSecondaryLink:
        selectedTaxonomy?.key === VENUES_TAXONOMY_KEY
          ? SecondaryNavIdentifier.WEDDING_VENUES
          : SecondaryNavIdentifier.EXPLORE_VENDORS,
      activeTertiaryLink: selectedTaxonomy?.key as TertiaryNavIdentifier,
    };
  }, [parsedUrl]);

  // Trigger experiment viewed, which needs to happen on the client
  useEffect(() => {
    if (!identifiedAsVendor) {
      EXPERIMENT_FLAGS.isUnderTest('marketplace_featured_vendors_holdout', userContext, true);
    }
  }, [identifiedAsVendor, userContext]);

  return (
    <UserContextProvider userContext={userContext}>
      <ResponsiveDesignProvider userAgent={userAgent}>
        <Provider store={store}>
          <MainLayout
            activeSecondaryLink={activeSecondaryLink}
            activeTertiaryLink={activeTertiaryLink}
            ssrNavHtml={navHtml}
            showSecondaryNavOnLoad
            showAppDownloadBanner
            className={styles.mainLayout}
            id="main"
          >
            <PreAuthSearchResultsPageProvider
              {...props}
              identifiedAsVendor={identifiedAsVendor}
              parsedUrl={parsedUrl}
            >
              <PreAuthSearchResultsSeoProvider>
                <PreAuthSearchResultsPage />
              </PreAuthSearchResultsSeoProvider>
            </PreAuthSearchResultsPageProvider>
          </MainLayout>
        </Provider>
      </ResponsiveDesignProvider>
    </UserContextProvider>
  );
};

export const getServerSideProps: GetServerSideProps<PreAuthSearchResultsPageProps> = async (
  context
) => {
  const slug = Array.isArray(context.query.slug) ? context.query.slug[0] : context.query.slug ?? '';
  const pathSlugs = findSlugsInPath(slug);
  const { location, taxonomyKey } = locationAndCategorySlugInPath(pathSlugs);

  // Fetch the properties that are 100% static and have no dependencies
  const [navHtml, userContext, taxonomiesWithFacets, selectedLocation] = await Promise.all([
    getNavHtml(context.req),
    getUserContext(context.req),

    // The searchable facets drive the filters on the page
    getSearchableFacets(taxonomyKey),

    // The location parsed out of the slug and validated with the backend
    location ? getSearchLocationForSlug(location, ApiService) : Promise.resolve(ALL_MARKETS),
  ]);

  // Transform the taxonomies (with their allowed facets) into a static lookup that lets us easily
  // find the facets for a given taxonomy
  const facetsByVendorType = getFacetsByVendorType(taxonomiesWithFacets);

  // Parse the URL into a path and query string
  // We remove `_preauth` from the path because it's not part of the visible URL
  const requestUrl = (context.req.url || '').replace('/_preauth', '');
  const { query } = queryString.parseUrl(requestUrl, {
    arrayFormat: 'bracket',
  });

  // Parse the URL into a more usable format: the vendorSearch, the canonical url, and all the
  // selected facets. This is the main driver of the page (the URL is the source of truth for all
  // the inputs to search). This validates the slugs in the URL and lets us know if anything is
  // invalid (via the canonical URL).
  const parsedUrl = parseVendorSearchUrl({
    slug,
    query,
    facetsByVendorType,
    selectedLocation,
    searchName: 'PRE_AUTH_SRP',
    seoOptimized: true,
    allowUnclaimedListings: false,
  });

  // Here is where we find out of any of the parameters in the URL were invalid and we redirect to
  // the canonical URL if they were
  if (shouldRedirect(requestUrl, parsedUrl.canonicalUrl)) {
    return redirect(parsedUrl.canonicalUrl);
  }

  const preAuthSrpProsAndCons = ['TEST', 'TEST-0', 'TEST-1'].includes(
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace-preauth-srp-pros-and-cons-release-ramp',
      userContext,
      context.req.cookies || {}
    )
  );

  // Now that we have the search (parsedUrlToUse.vendorSearch) we can execute queries that are
  // related to search. Some of these are static for the page and some are specific to the user.

  // Static data for the page based on the city + taxonomy (and facets selected)
  const [
    adjacentVendors,
    realWeddings,
    expertAdvice,
    searchLocationReviews,
    seoContent,
    seoTopLocations,
    seoNearbyCities,
    seoMetrics,
  ] = await fetchPreAuthSearchResultsPageData(parsedUrl.vendorSearch, ApiService);

  // Build the search filters based on the taxonomy
  const searchFilters = getSearchFilters(parsedUrl.vendorSearch, taxonomiesWithFacets);

  // Determine if a vendor is viewing the page or if the couple is in the holdout group. Couples
  // in the holdout group won't see featured vendors. Since vendors have paid for featured slots,
  // we always want to show them featured vendors.
  const identifiedAsVendor = isVendorIdentified((key: string) =>
    Boolean((context.req.cookies || {})[key])
  );

  const isInHoldoutGroupExperiment =
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace_featured_vendors_holdout',
      userContext,
      context.req.cookies || {}
    ) === 'TEST-1';

  // Build and execute the search request. This is finally running the vendor search and getting
  // us card views we can render on the page.
  const vendorSearchRequest = buildBackendVendorSearchRequestFromSearchProperties({
    vendorSearch: parsedUrl.vendorSearch,
    pageSize: PRE_AUTH_PAGE_SIZE,
    shouldInsertMiniQuiz: false,
    shouldInsertBudgetQuiz: false,
    isInHoldoutGroupExperiment,
    identifiedAsVendor,
  });

  const searchResponse = await fetchStorefrontSearchResults(vendorSearchRequest, userContext);

  // Transform the search response into the form used on the page
  const searchResults: SearchResultPageSearchResult = {
    totalHits: searchResponse.totalHits,
    offset: searchResponse.offset,
    vendors: searchResponse.entities,
    shouldInsertMiniQuiz: false,
    shouldInsertBudgetQuiz: false,
  };

  return {
    props: {
      // Page props
      navHtml,
      userAgent: context.req.headers['user-agent'],
      userContext,
      // Page context props
      parsedUrl,
      taxonomiesWithFacets,
      facetsByVendorType,
      searchFilters,
      searchResults,
      adjacentVendors,
      expertAdvice,
      realWeddings,
      searchLocationReviews,
      seoMetrics,
      seoContent,
      seoTopLocations,
      seoNearbyCities,
      preAuthSrpProsAndCons,
      isInHoldoutGroupExperiment,
      identifiedAsVendor,
      loading: false,
    },
  };
};

export default Page;
